import { useApolloClient } from '@apollo/client';

import {
  BillingCompanyBillingInfoDocument,
  BillingCompanyBillingInfoQuery,
  LicenseInfoTypeFragment,
  LicensePaymentState,
  OrganizationDiscountPureType,
  PaymentScheme,
  PointPureType,
  SubscriptionStatus,
  UserBillingInfo,
  useBillingCompanyBillingInfoLazyQuery,
} from '@shared/api/graphql/generated';
import { FetchPolicyKeys } from '@shared/api/graphql/types';

export { SubscriptionStatus, LicensePaymentState };

export type { LicenseInfoTypeFragment };

export type PointLicenseType = Pick<PointPureType, 'id' | 'address' | 'playingStatus' | 'isEnabled'>;

export type LicenseType = LicenseInfoTypeFragment & {
  point?: PointLicenseType | null;
};

export type BillingInfoLocalType = Pick<
  UserBillingInfo,
  | 'isBillingEnabled'
  | 'hasTrialLicenses'
  | 'hasActiveLicenses'
  | 'hasInactiveLicenses'
  | 'hasWaitPaymentLicenses'
  | 'hasGraceLicenses'
  | 'isLastInvoiceHasZeroAmount'
  | 'isWaitPaymentLicense'
  | 'isGraceLicense'
  | 'paymentMethod'
  | 'canRealizeBillingOperation'
  | 'canRealizeBillingOperationWithPayment'
  | 'selectedPaymentKind'
>;

export type BillingInfoType = BillingInfoLocalType & {
  status?: SubscriptionStatus;
  firstPaidAt?: string;
  nextBillingAmount?: number;
  nextBillingPriceAmount?: number;
  nextBillingDate: string;
  renewableLicenses: LicenseType[];
  nonRenewableLicenses: LicenseType[];
  unpaidLicenses: LicenseType[];
  nextBillingLicenses: LicenseType[];
  totalLicenses: LicenseType[];
  expiresLicenseDate: string;
  discount: OrganizationDiscountPureType | null;
  hasRenewableLicenses: boolean;
  hasNonRenewableLicenses: boolean;
  paymentScheme: PaymentScheme;
  fullPeriodAmount?: number;
  fullPeriodAmountWithDiscount?: number;
};

export type UseUserBillingInfoType = {
  getBillingInfo: (companyId: string) => Promise<BillingInfoType>;
  watchBillingInfo: (companyId: string) => BillingInfoType;
};

const useUserBillingInfo = (): UseUserBillingInfoType => {
  const client = useApolloClient();

  const [query] = useBillingCompanyBillingInfoLazyQuery({
    fetchPolicy: FetchPolicyKeys.CACHE_AND_NETWORK,
  });

  const getBillingInfo = async (companyId: string) => {
    const result = await query({
      variables: {
        companyId,
      },
    });

    return result.data?.result as BillingInfoType;
  };

  const watchBillingInfo = (companyId: string) => {
    const observableQuery = client.watchQuery<BillingCompanyBillingInfoQuery | undefined>({
      query: BillingCompanyBillingInfoDocument,
      variables: {
        companyId,
      },
    });

    const result = observableQuery.getCurrentResult();

    return (result.data?.result || {}) as BillingInfoType;
  };

  return { getBillingInfo, watchBillingInfo };
};

export default useUserBillingInfo;
